import React, { useState, useEffect, useRef } from 'react';
import { userAppContext } from '../../Context/Context';
import { fetchAuthSession, updateUserAttributes } from 'aws-amplify/auth';
import './ChatWidget.css';

const STORAGE_KEY = 'chatWidgetHistory';
const CONSENT_KEY = 'chatWidgetConsent';
const WIDGET_VERSION = '2.0';

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: string;
  isConsentRequest?: boolean;
}

interface ChatPayload {
  message: string;
  isAuthenticated: boolean;
  userEmail?: string;
}

export default function ChatWidget() {
  const context = userAppContext();
  const isAuthenticated = context?.isAuthenticated || false;
  const userEmail = context?.user?.email || null;
  const [isOpen, setIsOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackConsent, setFeedbackConsent] = useState<boolean | null>(null);
  const [pendingFeedbackMessage, setPendingFeedbackMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const WELCOME_MESSAGE: Message = {
    id: 'welcome',
    sender: 'bot',
    text: "Hello! I'm the Support Bot, your website navigation assistant. You can ask me where to find things on the page, or leave feedback. For scientific data questions, visit our AIDA at /Assistant",
    timestamp: new Date().toISOString(),
  };

  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      const savedVersion = sessionStorage.getItem('chatWidgetVersion');

      if (saved && savedVersion === WIDGET_VERSION) {
        const parsed = JSON.parse(saved);
        if (parsed.length > 0) {
          return parsed;
        }
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(CONSENT_KEY);
        sessionStorage.setItem('chatWidgetVersion', WIDGET_VERSION);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
    return [WELCOME_MESSAGE];
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  }, [messages]);

  useEffect(() => {
    if (!isAuthenticated && messages.length > 1) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(CONSENT_KEY);
      setMessages([WELCOME_MESSAGE]);
      setFeedbackConsent(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadFeedbackConsent = async () => {
      if (isAuthenticated && userEmail) {
        try {
          const savedConsent = sessionStorage.getItem(CONSENT_KEY);
          if (savedConsent !== null) {
            setFeedbackConsent(savedConsent === 'true');
            return;
          }

          const session = await fetchAuthSession();
          const consent = session.tokens?.idToken?.payload['custom:feedback_consent'];
          const consentValue = consent === 'true';
          setFeedbackConsent(consentValue);
          sessionStorage.setItem(CONSENT_KEY, String(consentValue));
        } catch (error) {
          console.error('Error loading feedback consent:', error);
          setFeedbackConsent(null);
        }
      } else {
        setFeedbackConsent(null);
      }
    };
    loadFeedbackConsent();
  }, [isAuthenticated, userEmail]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const saveFeedbackConsent = async (consent: boolean) => {
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:feedback_consent': consent ? 'true' : 'false',
        },
      });
      setFeedbackConsent(consent);
      sessionStorage.setItem(CONSENT_KEY, String(consent));
    } catch (error) {
      console.error('Error saving feedback consent:', error);
    }
  };

  const handleConsentResponse = async (consent: boolean) => {
    await saveFeedbackConsent(consent);

    const responseMessage: Message = {
      id: `bot-consent-response-${Date.now()}`,
      sender: 'bot',
      text: consent
        ? 'Thank you! Your feedback will include your email for follow-up.'
        : 'Understood. Your feedback will be sent anonymously.',
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, responseMessage]);

    if (pendingFeedbackMessage) {
      await sendFeedbackToBackend(pendingFeedbackMessage);
      setPendingFeedbackMessage(null);
    }
  };

  const sendFeedbackToBackend = async (message: string) => {
    try {
      const context = formatConversationContext(messages);
      const messageWithContext = `${message}\n\n--- Previous Conversation ---\n${context}`;

      const payload: ChatPayload = {
        message: messageWithContext,
        isAuthenticated: isAuthenticated || false,
      };

      if (isAuthenticated && userEmail) {
        payload.userEmail = userEmail;
      }

      const response = await fetch(`${process.env.REACT_APP_CHATBOT_API_URL}/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Network error');

      const data = await response.json();

      const botMessage: Message = {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        text: data.message,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}`,
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleSendMessage = async () => {
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: trimmedMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const messageWithContext = createMessageWithContext(trimmedMessage, [...messages, userMessage]);

      const payload: ChatPayload = {
        message: messageWithContext,
        isAuthenticated: isAuthenticated || false,
      };

      if (isAuthenticated && userEmail) {
        payload.userEmail = userEmail;
      }

      const response = await fetch(`${process.env.REACT_APP_CHATBOT_API_URL}/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Network error');

      const data = await response.json();

      if (data.isFeedback && isAuthenticated && feedbackConsent === null) {
        setPendingFeedbackMessage(trimmedMessage);
        const consentMessage: Message = {
          id: `bot-consent-${Date.now()}`,
          sender: 'bot',
          text: 'To help us follow up on your feedback, would you like to include your email address? This is optional and you can change this preference anytime.',
          timestamp: new Date().toISOString(),
          isConsentRequest: true,
        };
        setMessages(prev => [...prev, consentMessage]);
      } else {
        const botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          text: data.message,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}`,
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatMessageWithLinks = (text: string): string => {
    return text
      .replace(/\/([\w-]+)/g, match => {
        return `<a href="${match}" class="chat-link" target="_self">${match}</a>`;
      })
      .replace(/(https?:\/\/[^\s]+)/g, match => {
        return `<a href="${match}" class="chat-link" target="_blank" rel="noopener noreferrer">${match}</a>`;
      });
  };

  const formatConversationContext = (messages: Message[]): string => {
    const relevantMessages = messages.filter(msg => !msg.isConsentRequest && msg.id !== 'welcome').slice(-5);

    return relevantMessages
      .map(msg => {
        const time = formatTime(msg.timestamp);
        const sender = msg.sender === 'user' ? 'User' : 'Assistant';
        return `[${time}] ${sender}: ${msg.text}`;
      })
      .join('\n');
  };

  const createMessageWithContext = (originalMessage: string, currentMessages: Message[]): string => {
    const context = formatConversationContext(currentMessages);

    if (context.trim() === '') {
      return originalMessage;
    }

    const messageWithContext = `${originalMessage}\n\n--- Previous Conversation ---\n${context}`;
    return messageWithContext;
  };

  return (
    <>
      <div className={`chat-widget-container ${isOpen ? 'open' : ''}`}>
        {isOpen && (
          <div className="chat-widget-window">
            <div className="chat-widget-header">
              <div className="chat-widget-header-content">
                <i className="bi bi-robot"></i>
                <span>Support Bot</span>
              </div>
              <button className="chat-widget-close" onClick={() => setIsOpen(false)} aria-label="Close chat">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="chat-widget-disclaimer">
              <small className="text-muted">
                This assistant uses Groq-accelerated models to provide responses based on DISGENET website.
                The information generated may contain inaccuracies or hallucinations and should be
                independently verified.
              </small>
            </div>

            <div className="chat-widget-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`chat-widget-message ${msg.sender}`}>
                  <div className="chat-widget-message-content">
                    <p dangerouslySetInnerHTML={{ __html: formatMessageWithLinks(msg.text) }} />
                    <span className="chat-widget-message-time">{formatTime(msg.timestamp)}</span>
                    {msg.isConsentRequest && (
                      <div className="chat-widget-consent-buttons">
                        <button
                          className="chat-widget-consent-btn accept"
                          onClick={() => handleConsentResponse(true)}
                        >
                          Yes, include my email
                        </button>
                        <button
                          className="chat-widget-consent-btn decline"
                          onClick={() => handleConsentResponse(false)}
                        >
                          No, send anonymously
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-widget-message bot">
                  <div className="chat-widget-message-content">
                    <div className="chat-widget-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-widget-input-container">
              <textarea
                className="chat-widget-input"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={1}
                disabled={isLoading}
              />
              <button
                className="chat-widget-send"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Send message"
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        className={`chat-widget-toggle ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open chat"
      >
        <i className="bi bi-chat-dots-fill"></i>
      </button>
    </>
  );
}
